import { Head, Link, router } from "@inertiajs/react"
import {
  Plus,
  MoreHorizontal,
  Grid3x3,
  List,
  Eye,
  Pencil,
  Coffee,
  Trash2,
  Search,
} from "lucide-react"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import {
  cuppingsPath,
  editCuppingPath,
  cuppingPath,
  newCuppingPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface Sample {
  id: number
  name: string
  description: string
  origin: string
  land: string
  producer: string
  process: number
  variety: number
}

interface User {
  id: number
  name: string
  email: string
}

interface Tasting {
  id: number
  title: string
  details: string
  created_at: string
  start_date?: string
  location?: string
  cupping_form: string
  samples: Sample[]
  user?: User
  has_user_cupping?: boolean
}

interface Props {
  cuppings: Tasting[]
  search?: string
}

export default function TastingsIndex({ cuppings, search }: Props) {
  const { t } = useTranslation()
  const tastings = cuppings

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("cuppings.breadcrumb"),
      href: cuppingsPath(),
    },
  ]
  const [selectedTastings, setSelectedTastings] = useState<number[]>([])
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [tastingToDelete, setTastingToDelete] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState(search || "")
  const [viewMode, setViewMode] = useState<"list" | "grid">("list")

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      router.get(
        cuppingsPath(),
        { search: searchQuery },
        {
          preserveState: true,
          preserveScroll: true,
          only: ["cuppings"],
        },
      )
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
  }

  const clearSearch = () => {
    setSearchQuery("")
  }

  const openDeleteModal = (id: number) => {
    setTastingToDelete(id)
    setDeleteModalOpen(true)
  }

  const handleDelete = () => {
    if (tastingToDelete) {
      router.delete(cuppingPath(tastingToDelete), {
        onSuccess: () => {
          setDeleteModalOpen(false)
          setTastingToDelete(null)
        },
      })
    }
  }

  const toggleSelectAll = () => {
    if (selectedTastings.length === tastings.length) {
      setSelectedTastings([])
    } else {
      setSelectedTastings(tastings.map((t) => t.id))
    }
  }

  const toggleSelect = (id: number) => {
    if (selectedTastings.includes(id)) {
      setSelectedTastings(selectedTastings.filter((tid) => tid !== id))
    } else {
      setSelectedTastings([...selectedTastings, id])
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("cuppings.title")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div>
          <p className="text-muted-foreground mb-2 text-sm">
            {t("cuppings.breadcrumb")}
          </p>
          <h1 className="text-3xl font-bold">{t("cuppings.title")}</h1>
        </div>

        <Card>
          <CardContent className="p-6">
            {/* Toolbar */}
            <div className="mb-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link href={newCuppingPath()}>
                    <Button className="bg-primary hover:bg-primary/90">
                      <Plus className="mr-2 h-4 w-4" />
                      {t("cuppings.new_cupping")}
                    </Button>
                  </Link>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant={viewMode === "list" ? "default" : "outline"}
                    size="icon"
                    onClick={() => setViewMode("list")}
                    className={
                      viewMode === "list"
                        ? ""
                        : "text-primary hover:text-primary/80"
                    }
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "grid" ? "default" : "outline"}
                    size="icon"
                    onClick={() => setViewMode("grid")}
                    className={
                      viewMode === "grid"
                        ? ""
                        : "text-primary hover:text-primary/80"
                    }
                  >
                    <Grid3x3 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative max-w-md">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  type="text"
                  placeholder={t("cuppings.search_placeholder")}
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-1/2 right-1 h-7 -translate-y-1/2 px-2"
                    onClick={clearSearch}
                  >
                    {t("common.cancel")}
                  </Button>
                )}
              </div>
            </div>

            {/* Samples Tab */}
            <div className="mb-4">
              <Button
                variant="ghost"
                className="text-primary hover:text-primary/90"
              >
                {t("samples.title")}
              </Button>
            </div>

            {/* Table */}
            {tastings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="bg-primary/10 mb-4 rounded-full p-6">
                  {searchQuery ? (
                    <Search className="text-primary h-12 w-12" />
                  ) : (
                    <Coffee className="text-primary h-12 w-12" />
                  )}
                </div>
                <h3 className="mb-2 text-lg font-semibold">
                  {searchQuery
                    ? t("common.no_results")
                    : t("cuppings.no_cuppings")}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {searchQuery
                    ? `${t("common.no_results")}`
                    : t("cuppings.no_cuppings")}
                </p>
                {searchQuery ? (
                  <Button
                    variant="outline"
                    onClick={clearSearch}
                    className="border-primary text-primary hover:bg-primary/10"
                  >
                    {t("common.cancel")}
                  </Button>
                ) : (
                  <Link href="/cuppings/new">
                    <Button className="bg-primary hover:bg-primary/90">
                      <Plus className="mr-2 h-4 w-4" />
                      {t("cuppings.new_cupping")}
                    </Button>
                  </Link>
                )}
              </div>
            ) : viewMode === "list" ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b">
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedTastings.length === tastings.length}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead>
                        {t("metrics.recent_cuppings.organizer")}
                      </TableHead>
                      <TableHead>{t("metrics.recent_cuppings.date")}</TableHead>
                      <TableHead>{t("cuppings.fields.title")}</TableHead>
                      <TableHead>{t("cuppings.fields.cupping_form")}</TableHead>
                      <TableHead>{t("cuppings.fields.samples")}</TableHead>
                      <TableHead>{t("common.actions")}</TableHead>
                      <TableHead>{t("common.edit")}</TableHead>
                      <TableHead>{t("common.delete")}</TableHead>
                      <TableHead>{t("cuppings.actions.report")}</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tastings.map((tasting) => (
                      <TableRow key={tasting.id} className="border-b">
                        <TableCell>
                          <Checkbox
                            checked={selectedTastings.includes(tasting.id)}
                            onCheckedChange={() => toggleSelect(tasting.id)}
                          />
                        </TableCell>
                        <TableCell>
                          {tasting.user?.name || "Desconocido"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {tasting.start_date
                            ? new Date(tasting.start_date).toLocaleString(
                                "es",
                                {
                                  month: "2-digit",
                                  day: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )
                            : new Date(tasting.created_at).toLocaleString(
                                "es",
                                {
                                  month: "2-digit",
                                  day: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {tasting.title}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {{
                              arabica: "Arábica",
                              robusta: "Robusta",
                              cup_of_excellence: "Taza de Excelencia",
                              sca_cva_affective: "SCA CVA Afectivo",
                              sca_cva_descriptive: "SCA CVA Descriptivo",
                              sca_cva_combined: "SCA CVA Combinado",
                            }[tasting.cupping_form] ||
                              tasting.cupping_form ||
                              "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className="bg-primary/10 text-primary"
                          >
                            <Coffee className="mr-1 h-3 w-3" />
                            {tasting.samples?.length || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {tasting.has_user_cupping ? (
                            <Link href={`${cuppingPath(tasting.id)}/report`}>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-primary text-primary hover:bg-primary/10"
                              >
                                {t("cuppings.actions.report")}
                              </Button>
                            </Link>
                          ) : (
                            <Link href={`${cuppingPath(tasting.id)}/cup`}>
                              <Button
                                size="sm"
                                className="bg-primary hover:bg-primary/90"
                              >
                                {t("cuppings.actions.cup")}
                              </Button>
                            </Link>
                          )}
                        </TableCell>
                        <TableCell>
                          <Link href={editCuppingPath(tasting.id)}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-primary hover:text-primary/80 h-8 w-8"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive h-8 w-8"
                            onClick={() => openDeleteModal(tasting.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Link href={cuppingPath(tasting.id)}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-primary hover:text-primary/80 h-8 w-8"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  router.visit(cuppingPath(tasting.id))
                                }
                              >
                                Ver
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  router.visit(editCuppingPath(tasting.id))
                                }
                              >
                                Editar Sesión
                              </DropdownMenuItem>
                              {tasting.has_user_cupping ? (
                                <>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      router.visit(
                                        `${cuppingPath(tasting.id)}/cup`,
                                      )
                                    }
                                  >
                                    Editar Catación
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() =>
                                      router.visit(
                                        `${cuppingPath(tasting.id)}/report`,
                                      )
                                    }
                                  >
                                    {t("cuppings.actions.report")}
                                  </DropdownMenuItem>
                                </>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() =>
                                    router.visit(
                                      `${cuppingPath(tasting.id)}/cup`,
                                    )
                                  }
                                >
                                  Catar Ahora
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => openDeleteModal(tasting.id)}
                                className="text-destructive"
                              >
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button variant="ghost" size="icon" disabled>
                    &lt;
                  </Button>
                  <Button variant="default" size="sm" className="bg-primary">
                    1
                  </Button>
                  <Button variant="ghost" size="icon" disabled>
                    &gt;
                  </Button>
                </div>
              </div>
            ) : (
              /* Grid View */
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tastings.map((tasting) => (
                  <Card key={tasting.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="mb-3 flex items-start justify-between">
                        <div>
                          <h3 className="truncate font-semibold">
                            {tasting.title}
                          </h3>
                          <p className="text-muted-foreground text-sm">
                            {tasting.start_date
                              ? new Date(tasting.start_date).toLocaleDateString(
                                  "es",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )
                              : new Date(tasting.created_at).toLocaleDateString(
                                  "es",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )}
                          </p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                router.visit(cuppingPath(tasting.id))
                              }
                            >
                              Ver
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                router.visit(editCuppingPath(tasting.id))
                              }
                            >
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openDeleteModal(tasting.id)}
                              className="text-destructive"
                            >
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="mb-3 flex items-center gap-2">
                        <Badge
                          variant="secondary"
                          className="text-xs capitalize"
                        >
                          {{
                            arabica: "Arábica",
                            robusta: "Robusta",
                            cup_of_excellence: "Taza de Excelencia",
                            sca_cva_affective: "SCA CVA Afectivo",
                            sca_cva_descriptive: "SCA CVA Descriptivo",
                            sca_cva_combined: "SCA CVA Combinado",
                          }[tasting.cupping_form] ||
                            tasting.cupping_form ||
                            "N/A"}
                        </Badge>
                        <Badge
                          variant="secondary"
                          className="bg-primary/10 text-primary text-xs"
                        >
                          <Coffee className="mr-1 h-3 w-3" />
                          {tasting.samples?.length || 0}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground text-xs">
                          {tasting.user?.name || "Desconocido"}
                        </span>
                        {tasting.has_user_cupping ? (
                          <Link href={`${cuppingPath(tasting.id)}/report`}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-primary text-primary hover:bg-primary/10 h-7 text-xs"
                            >
                              Ver Reporte
                            </Button>
                          </Link>
                        ) : (
                          <Link href={`${cuppingPath(tasting.id)}/cup`}>
                            <Button
                              size="sm"
                              className="bg-primary hover:bg-primary/90 h-7 text-xs"
                            >
                              Catar ahora
                            </Button>
                          </Link>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("cuppings.index.delete_modal.title")}
              </DialogTitle>
              <DialogDescription>
                {t("cuppings.index.delete_modal.description")}{" "}
                {tastingToDelete &&
                tastings.find((t) => t.id === tastingToDelete)?.title
                  ? `"${tastings.find((t) => t.id === tastingToDelete)?.title}"`
                  : t("cuppings.index.delete_modal.description_fallback")}
                {t("cuppings.index.delete_modal.description_suffix")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteModalOpen(false)}
              >
                {t("cuppings.index.delete_modal.cancel")}
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                {t("cuppings.index.delete_modal.delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  )
}
